import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@/components/core/avatar.tsx";
import { Button } from "@/components/core/buttons/button.tsx";
import { useModalControl } from "@/components/core/modals/use-modal-controller.ts";
import { useModal } from "@/components/core/modals/use-modal.ts";
import { useDebounceEffect } from "@/hooks/use-debounce-effect.ts";
import { canvasPreview } from "@/utils/canvas-preview.ts";
import { centerAspectCrop } from "@/utils/center-aspect-crop.ts";
import { type ChangeEvent, useRef, useState } from "react";
import type { Crop, PixelCrop } from "react-image-crop";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

type ImageSelectProps = {
	initials?: string;
	avatar: string | null;
	hasLabel?: boolean;
	size?: 50 | 90;
	onCrop: (image: string) => void;
	onImageRemove?: () => void;
};

export function CropperModal({
	image,
	onCrop,
}: {
	image: string;
	onCrop: (image: string) => void;
}) {
	const imgRef = useRef<HTMLImageElement>(null);
	const blobUrlRef = useRef("");
	const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
	const previewCanvasRef = useRef<HTMLCanvasElement>(null);
	const scale = 1;
	const rotate = 0;
	const [crop, setCrop] = useState<Crop | undefined>(undefined);
	const modal = useModalControl();

	useDebounceEffect(
		async () => {
			if (
				completedCrop?.width &&
				completedCrop.height &&
				imgRef.current &&
				previewCanvasRef.current
			) {
				canvasPreview(
					imgRef.current,
					previewCanvasRef.current,
					completedCrop,
					scale,
					rotate,
				);
			}
		},
		100,
		[completedCrop, scale, rotate],
	);

	async function handleCropImage() {
		const imageElement = imgRef.current;
		const previewCanvas = previewCanvasRef.current;
		if (!imageElement || !previewCanvas || !completedCrop) {
			throw new Error("Crop canvas does not exist");
		}

		const scaleX = imageElement.naturalWidth / imageElement.width;
		const scaleY = imageElement.naturalHeight / imageElement.height;

		const offscreen = new OffscreenCanvas(
			completedCrop.width * scaleX,
			completedCrop.height * scaleY,
		);
		const ctx = offscreen.getContext("2d");
		if (!ctx) {
			throw new Error("No 2d context");
		}

		ctx.drawImage(
			previewCanvas,
			0,
			0,
			previewCanvas.width,
			previewCanvas.height,
			0,
			0,
			offscreen.width,
			offscreen.height,
		);

		const blob = await offscreen.convertToBlob({
			type: "image/png",
		});

		if (blobUrlRef.current) {
			URL.revokeObjectURL(blobUrlRef.current);
		}
		blobUrlRef.current = URL.createObjectURL(blob);

		const base64Image = previewCanvas.toDataURL("image/png");
		onCrop(base64Image);
	}

	const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		const { width, height } = e.currentTarget;
		setCrop(centerAspectCrop(width, height, 1));
	};

	return (
		<div className="flex flex-col w-full gap-4">
			<div className="flex justify-center items-center rounded-md bg-black overflow-hidden">
				<ReactCrop
					className="flex justify-center items-center"
					aspect={1}
					minHeight={200}
					keepSelection={true}
					minWidth={200}
					circularCrop={true}
					crop={crop}
					onComplete={(c) => setCompletedCrop(c)}
					onChange={(_, percentCrop) => setCrop(percentCrop)}
				>
					<img
						className="block h-full !max-h-[400px]"
						ref={imgRef}
						alt="Avatar"
						src={image}
						onLoad={onImageLoad}
					/>
				</ReactCrop>
			</div>

			<canvas className="hidden" ref={previewCanvasRef} />

			<div>
				<Button onClick={() => modal.closeModal()} variant="light" color="gray">
					Cancel Crop
				</Button>
				<Button onClick={handleCropImage}>Set Image</Button>
			</div>
		</div>
	);
}

export default function ImageSelect({
	onImageRemove,
	initials,
	onCrop,
	avatar = null,
}: ImageSelectProps) {
	const [image, setImage] = useState<string | null>(avatar);
	const modal = useModal();

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();

		const files = e.target.files;
		if (files && files.length > 0) {
			const fileBlob = files[0];
			const objectURL = URL.createObjectURL(fileBlob);

			const modalId = modal.openModal({
				title: "Crop your new profile picture",
				component: (
					<CropperModal
						onCrop={(objectURL) => {
							setImage(objectURL);
							onCrop(objectURL);
							modal.closeModal(modalId);
						}}
						image={objectURL}
					/>
				),
			});
		}
		e.target.value = "";
	};

	return (
		<div className="flex justify-center w-full p-8 rounded-md bg-slate-50">
			<div className="inline-flex flex-row items-center w-auto gap-4">
				<Avatar className="w-[100px] h-[100px] rounded-full bg-slate-300">
					<AvatarFallback>{initials}</AvatarFallback>
					<AvatarImage src={image || ""} />
				</Avatar>
				<div className="inline-flex flex-col w-auto gap-2">
					<div className="inline-flex flex-row w-auto gap-1">
						<div className="relative">
							<Button size="sm">Change</Button>
							<input
								id="user-photo"
								name="user-photo"
								type="file"
								accept="image/*"
								onChange={onChange}
								className="absolute inset-0 h-full w-full rounded-md opacity-0"
							/>
						</div>
						{image && (
							<Button
								type="button"
								onClick={() => {
									onImageRemove?.();
									setImage(null);
								}}
								variant="danger"
								size="sm"
							>
								Remove
							</Button>
						)}
					</div>
					<span className="text-base text-foreground/70">
						JPG, GIF or PNG. 5MB max.
					</span>
				</div>
			</div>
		</div>
	);
}
